import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, ChevronLeft, X, Upload, AlertCircle, CheckCircle, FileText, Loader2, Download, HelpCircle, Building2, UserPlus, Pencil, Trash2, ClipboardList } from 'lucide-react';
import PageHeader from './PageHeader';
import fileUploadService from '../../services/fileUploadService';
import crypto from 'crypto-js';
import toast from 'react-hot-toast';

const FileUpload = () => {
    const [files, setFiles] = useState([]);
    const [processedData, setProcessedData] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [showProcessedData, setShowProcessedData] = useState(false);
    const [confirmationData, setConfirmationData] = useState(null);
    const [showHowToUse, setShowHowToUse] = useState(false);
    const navigate = useNavigate();
    const { studyID } = useParams();

    const isValidCSV = (file) => {
        return file.type === 'text/csv' || file.name.endsWith('.csv');
    };

    const processCSV = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                const lines = text.split('\n').filter(line => line.trim() !== '');
                const headers = lines[0].split(',').map(header => header.trim());
                const data = [];

                for (let i = 1; i < lines.length; i++) {
                    const values = lines[i].split(',').map(value => value.trim());
                    if (values.length === headers.length) {
                        const entry = {};
                        headers.forEach((header, index) => {
                            entry[header] = values[index];
                        });
                        if (entry['Site']) {
                            data.push(entry);
                        }
                    }
                }

                resolve(data);
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    };

    const onDrop = async (acceptedFiles) => {
        const csvFiles = acceptedFiles.filter(isValidCSV);

        if (csvFiles.length !== acceptedFiles.length) {
            setUploadStatus({ type: 'error', message: 'Invalid file type. Please upload CSV files only.' });
            return;
        }

        setIsUploading(true);
        setUploadStatus(null);

        try {
            const processedData = await Promise.all(csvFiles.map(processCSV));
            const flattenedData = processedData.flat();

            if (flattenedData.length === 0) {
                setUploadStatus({ type: 'warning', message: 'No valid data found in the CSV file.' });
                setIsUploading(false);
                return;
            }

            setProcessedData(flattenedData);
            setFiles(csvFiles);

            const confirmationSummary = flattenedData.reduce((acc, item) => {
                const action = item['Transaction Flag'].toLowerCase();
                if (!acc[action]) acc[action] = 0;
                acc[action]++;
                return acc;
            }, {});

            setConfirmationData(confirmationSummary);
            setUploadStatus({ type: 'info', message: 'Please review the changes and confirm.' });
        } catch (error) {
            console.error('Error processing CSV:', error);
            setUploadStatus({ type: 'error', message: 'Error processing CSV. Please try again.' });
        } finally {
            setIsUploading(false);
        }
    };

    const calculateHash = (data) => {
        // Convert the data array to a JSON string without sorting
        const jsonString = JSON.stringify(data);
        console.log('JSON string to be hashed:', jsonString);
        
        // Calculate SHA-256 hash using CryptoJS
        const hash = crypto.SHA256(jsonString);
        const hashString = hash.toString(crypto.enc.Hex);
        console.log('Calculated hash:', hashString);

        return hashString;
    };

    const handleConfirm = async () => {
        setIsUploading(true);
        setUploadStatus(null);

        try {
            const hash = calculateHash(processedData);
            console.log('Final SHA256 Hash:', hash);

            // Use study ID from URL parameters instead of sponsor code
            const payload = {
                data: processedData,
                hash: hash,
                study_id: studyID
            };

            const response = await fileUploadService.uploadFile(payload);

            console.log('Server resoonse:', response); // logging entire response

            if (response) {
                const processedItems = response.processed_items || [];
                const unprocessedItems = response.unprocessed_items || [];

                // Clear confirmation dialog and parsed data so the form resets
                setConfirmationData(null);
                setProcessedData([]);
                setFiles([]);

                setUploadStatus({
                    type: 'info',
                    message: `Upload complete. ${processedItems.length} items processed, ${unprocessedItems.length} items unprocessed.`,
                    details: {
                        processed_items: processedItems,
                        unprocessed_items: unprocessedItems
                    }
                });

                toast.success(`Upload complete. ${processedItems.length} items processed, ${unprocessedItems.length} items unprocessed.`);
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (error) {
            console.error('Error uploading data:', error);
            setUploadStatus({ type: 'error', message: 'Error uploading data. Please try again.' });
            toast.error('Error uploading data. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };


    const handleCancel = () => {
        setConfirmationData(null);
        setProcessedData([]);
        setFiles([]);
        setUploadStatus(null);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    const removeFile = (index) => {
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };

    const downloadSampleCSV = () => {

        // TODO add second button for site csv 
        const adduserheaders = ['Site', 'Last Name', 'First Name', 'Role', 'Phone Number', 'Cell Phone Number', 'Fax Number', 'Email', 'Institution Name', 'Time Zone', 'GMT Offset', 'remarks', 'Transaction Flag'];        
        const usercsv = adduserheaders.join(',') + '\n';

        const addsiteheaders = ['Site', 'Site name in eCOA', 'Site Ready Date', 'Investigator Last Name', 'Investigator First Name', 'Institution Name', 'Address Line 1', 'Address line 2', 'City', 'State', 'Zip', 'Country', 'Phone Number', 'Investigator Cell Phone Number', 'Inv Fax Number', 'Inv Email', 'Study Coordinator Contact', 'SC Phone #', 'SC Email', 'SC Fax #', 'eCOA shipment Contact', 'eCOA shpment email', 'Time Zone', 'GMT Offset', 'Remarks', 'Transaction Flag'];
        const sitecsv = addsiteheaders.join(',') + '\n';
        const blob = new Blob([usercsv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'sample_upload.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };


    const statusStyles = {
        success: { container: 'border-success-100 dark:border-success-500/30', header: 'bg-success-50 dark:bg-success-500/10', icon: 'text-success-600 dark:text-success-400' },
        error: { container: 'border-danger-100 dark:border-danger-500/30', header: 'bg-danger-50 dark:bg-danger-500/10', icon: 'text-danger-600 dark:text-danger-400' },
        info: { container: 'border-primary-200 dark:border-primary-400/30', header: 'bg-primary-50 dark:bg-primary-400/10', icon: 'text-primary-600 dark:text-primary-400' },
        warning: { container: 'border-warning-500/30', header: 'bg-warning-50 dark:bg-warning-500/10', icon: 'text-warning-600 dark:text-warning-400' },
    };

    // Category tints per transaction type (blue add site, green add user,
    // amber edit site, red delete user).
    const toneStyles = {
        blue: { header: 'border-info-500 bg-info-50 dark:bg-info-500/10', icon: 'text-info-600 dark:text-info-400', index: 'bg-info-100 text-info-700 dark:bg-info-500/15 dark:text-info-400' },
        green: { header: 'border-success-500 bg-success-50 dark:bg-success-500/10', icon: 'text-success-600 dark:text-success-400', index: 'bg-success-100 text-success-700 dark:bg-success-500/15 dark:text-success-400' },
        yellow: { header: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10', icon: 'text-warning-600 dark:text-warning-400', index: 'bg-warning-100 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400' },
        red: { header: 'border-danger-500 bg-danger-50 dark:bg-danger-500/10', icon: 'text-danger-600 dark:text-danger-400', index: 'bg-danger-100 text-danger-700 dark:bg-danger-500/15 dark:text-danger-400' },
        gray: { header: 'border-gray-400 bg-gray-50 dark:border-gray-500 dark:bg-gray-900/40', icon: 'text-gray-500 dark:text-gray-400', index: 'bg-gray-200/70 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
    };

    const currentStatusStyle = uploadStatus ? (statusStyles[uploadStatus.type] || statusStyles.warning) : null;

     return (
        <div className="max-w-3xl mx-auto">
            <button
                onClick={() => navigate(-1)}
                className="back-link"
            >
                <ChevronLeft className="h-4 w-4" />
                Back to Study
            </button>

            {/* Header Section */}
            <PageHeader
                icon={Upload}
                title="Data Upload"
                subtitle="Upload and process your files to create users, study, and site records."
            >
                <button
                    onClick={downloadSampleCSV}
                    className="btn btn-secondary"
                >
                    <Download className="h-4 w-4" />
                    Download Template
                </button>
                <button
                    onClick={() => setShowHowToUse(!showHowToUse)}
                    aria-expanded={showHowToUse}
                    className="btn btn-secondary"
                >
                    <HelpCircle className="h-4 w-4" />
                    View Guide
                </button>
            </PageHeader>

            {/* Main Upload Section */}
            <div>
                <div className="relative mb-8">
                    <div
                        {...getRootProps()}
                        className={`group relative cursor-pointer rounded-xl border-2 border-dashed transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${
                            isDragActive
                                ? 'border-primary-500 bg-primary-50/60 dark:bg-primary-400/10'
                                : 'border-gray-300 bg-white hover:border-primary-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-primary-500 dark:hover:bg-gray-800'
                        }`}
                    >
                        <input {...getInputProps()} />
                        <div className="px-8 py-14 text-center">
                            <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl transition-colors duration-200 ${
                                isDragActive
                                    ? 'bg-primary-100/80 text-primary-600 dark:bg-primary-400/15 dark:text-primary-300'
                                    : 'bg-gray-100/80 text-gray-500 group-hover:bg-primary-50/80 group-hover:text-primary-600 dark:bg-gray-700/60 dark:text-gray-400 dark:group-hover:bg-primary-400/10 dark:group-hover:text-primary-300'
                            }`}>
                                <Upload className="h-5 w-5" />
                            </div>
                            <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                                {isDragActive
                                    ? "Drop your files here"
                                    : "Drag & drop your CSV files here"}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                or <span className="cursor-pointer font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300">browse</span> to choose files
                            </p>
                        </div>
                    </div>

                    {/* File List */}
                    {files.length > 0 && (
                        <div className="mt-6">
                            <div className="card divide-y divide-gray-100 dark:divide-gray-700">
                                {files.map((file, index) => (
                                    <div 
                                        key={index} 
                                        className="flex items-center justify-between px-5 py-3.5"
                                    >
                                        <div className="flex min-w-0 items-center">
                                            <div className="mr-4 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50 ring-1 ring-inset ring-gray-200 dark:bg-gray-700/60 dark:ring-gray-600">
                                                <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{file.name}</p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">{Math.round(file.size / 1024)} KB</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => removeFile(index)}
                                            aria-label="Remove file"
                                            className="btn btn-ghost btn-icon btn-sm ml-4"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Help Guide */}
                {showHowToUse && (
                    <div className="card mb-8 overflow-hidden">
                        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5 dark:border-gray-700">
                            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Upload Guide</h2>
                            <button 
                                onClick={() => setShowHowToUse(false)}
                                aria-label="Close guide"
                                className="btn btn-ghost btn-icon btn-sm"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="bg-gray-50/60 p-5 dark:bg-gray-900/40">
                            <div className="space-y-4">
                                {[
                                    "Download the CSV template using the button above",
                                    "Fill in your data following the template structure",
                                    "Ensure Protocol format follows: StudyTitle-StudyCode",
                                    "Set appropriate Transaction Flag (Add/Edit/Delete)",
                                    "Upload your completed CSV file",
                                    "Review and confirm the proposed changes"
                                ].map((step, index) => (
                                    <div key={index} className="flex items-start">
                                        <div className="mr-4 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 ring-1 ring-inset ring-primary-100 dark:bg-primary-400/10 dark:ring-primary-400/20">
                                            <span className="text-xs font-semibold text-primary-700 dark:text-primary-300">{index + 1}</span>
                                        </div>
                                        <p className="pt-1 text-sm text-gray-600 dark:text-gray-300">{step}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Status Messages */}
                {uploadStatus && (
                    <div className="mb-8">
                        <div className={`overflow-hidden rounded-xl border ${currentStatusStyle.container}`}>
                            <div className={`px-5 py-3.5 ${currentStatusStyle.header}`}>
                                <div className="flex items-center">
                                    {uploadStatus.type === 'success' && <CheckCircle className={`mr-3 h-5 w-5 flex-shrink-0 ${currentStatusStyle.icon}`} />}
                                    {uploadStatus.type === 'error' && <AlertCircle className={`mr-3 h-5 w-5 flex-shrink-0 ${currentStatusStyle.icon}`} />}
                                    {uploadStatus.type === 'info' && <AlertCircle className={`mr-3 h-5 w-5 flex-shrink-0 ${currentStatusStyle.icon}`} />}
                                    {uploadStatus.type === 'warning' && <AlertCircle className={`mr-3 h-5 w-5 flex-shrink-0 ${currentStatusStyle.icon}`} />}
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{uploadStatus.message}</span>
                                </div>
                            </div>
                            {uploadStatus.details && (
                                <div className="bg-white px-5 py-4 dark:bg-gray-800">
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Processed Items</h4>
                                            <div className="space-y-2">
                                                {uploadStatus.details.processed_items.map((item, index) => (
                                                    <div key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                                                        <CheckCircle className="mr-2 h-4 w-4 flex-shrink-0 text-success-600 dark:text-success-400" />
                                                        {`${item.action}: ${item.site_code || item.email}`}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        {uploadStatus.details.unprocessed_items.length > 0 && (
                                            <div>
                                                <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Failed Items</h4>
                                                <div className="space-y-3">
                                                    {uploadStatus.details.unprocessed_items.map((item, index) => (
                                                        <div key={index} className="rounded-lg bg-danger-50 p-3 dark:bg-danger-500/10">
                                                            <div className="text-sm text-gray-900 dark:text-gray-100">
                                                                {`${item['Transaction Flag']}: ${item['Site'] || item['Inv Email']}`}
                                                            </div>
                                                            <div className="mt-1 text-sm text-danger-600 dark:text-danger-400">
                                                                {item.error}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Confirmation Dialog */}
                {confirmationData && (
                    <div className="card mb-8 overflow-hidden">
                        <div className="border-b border-gray-100 px-5 py-3.5 dark:border-gray-700">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Review Changes</h3>
                        </div>
                        <div className="px-5 py-4">
                            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead>
                                        <tr>
                                            <th className="th-cell">
                                                Action
                                            </th>
                                            <th className="th-cell">
                                                Count
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
                                        {Object.entries(confirmationData).map(([action, count], index) => (
                                            <tr key={index} className="tr-interactive">
                                                <td className="td-cell capitalize text-gray-900 dark:text-gray-100">
                                                    {action}
                                                </td>
                                                <td className="td-cell">
                                                    <span className="badge badge-gray tabular-nums">
                                                        {count}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
                            <button
                                onClick={handleCancel}
                                className="btn btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirm}
                                className="btn btn-primary"
                            >
                                Confirm Changes
                            </button>
                        </div>
                    </div>
                )}

                {/* Processed Data */}
                {processedData.length > 0 && (
                    <div className="card overflow-hidden">
                        <button
                            className="flex w-full items-center justify-between px-5 py-3.5 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 dark:hover:bg-white/[0.03] dark:focus-visible:bg-white/[0.03]"
                            onClick={() => setShowProcessedData(!showProcessedData)}
                            aria-expanded={showProcessedData}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Processed Data</span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">({processedData.length} items)</span>
                            </div>
                            {showProcessedData ? (
                                <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                            ) : (
                                <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                            )}
                        </button>
                        {showProcessedData && (
                            <div className="border-t border-gray-100 dark:border-gray-700">
                                <div className="space-y-4 p-4">
                                    {/* Group items by transaction type */}
                                    {(() => {
                                        const grouped = processedData.reduce((acc, item) => {
                                            const flag = item['Transaction Flag']?.toLowerCase() || 'unknown';
                                            if (!acc[flag]) acc[flag] = [];
                                            acc[flag].push(item);
                                            return acc;
                                        }, {});

                                        const actionConfig = {
                                            'add site': { label: 'Add Sites', color: 'blue', icon: Building2 },
                                            'add user': { label: 'Add Users', color: 'green', icon: UserPlus },
                                            'edit site': { label: 'Edit Sites', color: 'yellow', icon: Pencil },
                                            'delete user': { label: 'Delete Users', color: 'red', icon: Trash2 },
                                        };

                                        return Object.entries(grouped).map(([flag, items]) => {
                                            const config = actionConfig[flag] || { label: flag, color: 'gray', icon: ClipboardList };
                                            const isSiteAction = flag.includes('site');
                                            const tone = toneStyles[config.color] || toneStyles.gray;
                                            const GroupIcon = config.icon;
                                            
                                            return (
                                                <div key={flag} className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <div className={`border-l-4 px-4 py-2 ${tone.header}`}>
                                                        <span className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                                            <GroupIcon className={`h-4 w-4 ${tone.icon}`} />
                                                            {config.label} ({items.length})
                                                        </span>
                                                    </div>
                                                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                                        {items.map((item, idx) => (
                                                            <div key={idx} className="flex items-center gap-4 bg-white px-4 py-3 dark:bg-gray-800">
                                                                <div className="flex-shrink-0">
                                                                    <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${tone.index}`}>
                                                                        {idx + 1}
                                                                    </span>
                                                                </div>
                                                                <div className="grid min-w-0 flex-1 grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                                                                    <div>
                                                                        <span className="text-gray-500 dark:text-gray-400">Site: </span>
                                                                        <span className="font-medium text-gray-900 dark:text-gray-100">{item.Site || '-'}</span>
                                                                    </div>
                                                                    {isSiteAction ? (
                                                                        <>
                                                                            <div>
                                                                                <span className="text-gray-500 dark:text-gray-400">Institution: </span>
                                                                                <span className="text-gray-900 dark:text-gray-100">{item['Institution Name'] || '-'}</span>
                                                                            </div>
                                                                            <div>
                                                                                <span className="text-gray-500 dark:text-gray-400">Timezone: </span>
                                                                                <span className="text-gray-900 dark:text-gray-100">{item['Time Zone'] || '-'}</span>
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div>
                                                                                <span className="text-gray-500 dark:text-gray-400">Name: </span>
                                                                                <span className="text-gray-900 dark:text-gray-100">
                                                                                    {item['First Name'] || item['Last Name'] 
                                                                                        ? `${item['First Name'] || ''} ${item['Last Name'] || ''}`.trim()
                                                                                        : '-'
                                                                                    }
                                                                                </span>
                                                                            </div>
                                                                            <div>
                                                                                <span className="text-gray-500 dark:text-gray-400">Role: </span>
                                                                                <span className="text-gray-900 dark:text-gray-100">{item.Role || '-'}</span>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                {!isSiteAction && item.Email && (
                                                                    <div className="hidden max-w-[200px] flex-shrink-0 truncate text-sm text-gray-500 dark:text-gray-400 sm:block">
                                                                        {item.Email}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Loading Overlay */}
            {isUploading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 dark:bg-black/70">
                    <div className="mx-auto max-w-sm rounded-xl bg-white px-8 py-6 shadow-overlay dark:bg-gray-800">
                        <div className="flex items-center justify-center gap-4">
                            <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
                            <div className="flex flex-col">
                                <span className="font-medium text-gray-900 dark:text-gray-100">Processing Files</span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">This may take a moment...</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUpload;